import React, { useEffect, useState, useCallback } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Plus, Search, UserSearch, ChevronRight, ArrowLeft, UserCheck, FileText } from 'lucide-react'
import { format } from 'date-fns'

const EVENT_TYPES = ['wedding', 'corporate', 'birthday', 'festival', 'community', 'other']
const LEAD_SOURCES = ['google', 'facebook', 'referral', 'returning', 'website', 'email', 'other']
const LEAD_STATUSES = ['new', 'contacted', 'quoted', 'converted', 'lost']

function extractEmailSubject(notes) {
  const match = String(notes || '').match(/Email Subject:\s*(.+)/i)
  return match ? match[1].trim() : ''
}

// ─── Lead Modal ──────────────────────────────────────────────────
function LeadModal({ lead, onClose, onSaved }) {
  const editing = !!lead?.id
  const [form, setForm] = useState({
    name: lead?.name || '',
    email: lead?.email || '',
    phone: lead?.phone || '',
    event_date: lead?.event_date || '',
    event_type: lead?.event_type || 'wedding',
    guest_count: lead?.guest_count || '',
    notes: lead?.notes || '',
    source: lead?.source || 'google',
    status: lead?.status || 'new',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  function set(field, val) { setForm(f => ({ ...f, [field]: val })) }

  async function handleSave(e) {
    e.preventDefault()
    if (!form.name.trim()) { setError('Name is required'); return }
    setSaving(true)
    const payload = { ...form, guest_count: form.guest_count ? parseInt(form.guest_count) : null }
    let result
    if (editing) {
      result = await supabase.from('leads').update(payload).eq('id', lead.id).select().single()
    } else {
      result = await supabase.from('leads').insert(payload).select().single()
    }
    if (result.error) { setError(result.error.message); setSaving(false) }
    else onSaved(result.data)
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 700 }}>{editing ? 'Edit Lead' : 'Add Lead'}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSave}>
          <div className="modal-body">
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-grid form-grid-2">
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input type="tel" className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input type="email" className="form-input" value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
            <div className="form-grid form-grid-2">
              <div className="form-group">
                <label className="form-label">Event Date</label>
                <input type="date" className="form-input" value={form.event_date} onChange={e => set('event_date', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Event Type</label>
                <select className="form-select" value={form.event_type} onChange={e => set('event_type', e.target.value)}>
                  {EVENT_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
            </div>
            <div className="form-grid form-grid-2">
              <div className="form-group">
                <label className="form-label">Guest Count (approx)</label>
                <input type="number" className="form-input" value={form.guest_count} onChange={e => set('guest_count', e.target.value)} min={0} />
              </div>
              <div className="form-group">
                <label className="form-label">How they heard</label>
                <select className="form-select" value={form.source} onChange={e => set('source', e.target.value)}>
                  {LEAD_SOURCES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Tent Needs / Notes</label>
              <textarea className="form-textarea" value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} placeholder="Describe what they're looking for…" />
            </div>
            {editing && (
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
                  {LEAD_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Leads List ──────────────────────────────────────────────────
export function LeadsPage() {
  const navigate = useNavigate()
  const [leads, setLeads] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [showModal, setShowModal] = useState(false)
  const [editLead, setEditLead] = useState(null)

  const loadLeads = useCallback(async () => {
    setLoading(true)
    let query = supabase.from('leads').select('*').order('created_at', { ascending: false })
    if (search.trim()) query = query.ilike('name', `%${search}%`)
    if (statusFilter !== 'all') query = query.eq('status', statusFilter)
    const { data } = await query
    setLeads(data || [])
    setLoading(false)
  }, [search, statusFilter])

  useEffect(() => {
    const t = setTimeout(loadLeads, 200)
    return () => clearTimeout(t)
  }, [loadLeads])

  async function ensureClientForLead(lead) {
    if (lead.converted_client_id) return lead.converted_client_id

    const { data: existingClient } = await supabase
      .from('clients')
      .select('id')
      .eq('email', lead.email)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (existingClient?.id) {
      await supabase.from('leads').update({ converted_client_id: existingClient.id }).eq('id', lead.id)
      return existingClient.id
    }

    const { data: newClient, error: clientErr } = await supabase.from('clients').insert({
      name: lead.name,
      email: lead.email,
      phone: lead.phone,
      source: 'lead-conversion',
      notes: lead.notes,
    }).select().single()
    if (clientErr) throw clientErr

    await supabase.from('leads').update({ converted_client_id: newClient.id }).eq('id', lead.id)
    return newClient.id
  }

  async function convertToClient(lead) {
    if (!window.confirm(`Convert "${lead.name}" to a client?`)) return
    try {
      const clientId = await ensureClientForLead(lead)
      await supabase.from('leads').update({ status: 'converted', converted_client_id: clientId }).eq('id', lead.id)
      loadLeads()
    } catch (err) {
      alert('Error creating client: ' + err.message)
    }
  }

  async function createQuoteFromLead(lead) {
    try {
      const clientId = await ensureClientForLead(lead)
      const params = new URLSearchParams({
        client: clientId,
        lead: lead.id,
      })
      navigate(`/quotes/new?${params.toString()}`)
    } catch (err) {
      alert('Could not start quote flow: ' + err.message)
    }
  }

  const statusCounts = LEAD_STATUSES.reduce((acc, s) => {
    acc[s] = leads.filter(l => l.status === s).length
    return acc
  }, {})

  return (
    <div data-testid="leads-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Leads</h1>
          <p className="page-subtitle">{leads.length} lead{leads.length !== 1 ? 's' : ''} found · {statusCounts.new || 0} new</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={16} /> Add Lead
        </button>
      </div>

      <div className="card" data-testid="inbound-lead-endpoint" style={{ marginBottom: 'var(--sp-4)' }}>
        <div className="card-body" style={{ padding: '12px 16px', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
          Inbound website leads endpoint: <code>/api/inbound-lead</code>
          <br />
          Inbound email intake endpoint: <code data-testid="inbound-email-endpoint">/api/inbound-email-lead</code>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 'var(--sp-4)' }}>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="search-bar">
            <Search size={14} className="search-icon" />
            <input type="text" placeholder="Search by name…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              className={`btn btn-sm ${statusFilter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setStatusFilter('all')}
            >All</button>
            {LEAD_STATUSES.map(s => (
              <button
                key={s}
                className={`btn btn-sm ${statusFilter === s ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setStatusFilter(s)}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="empty-state"><p>Loading…</p></div>
        ) : leads.length === 0 ? (
          <div className="empty-state" data-testid="empty-state-leads">
            <UserSearch size={40} className="empty-state-icon" />
            <p style={{ fontWeight: 600 }}>{search || statusFilter !== 'all' ? 'No leads match your current filters' : 'No leads yet'}</p>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              {search || statusFilter !== 'all' ? 'Try clearing filters or search to view more leads.' : 'Inbound website inquiries will appear here for review and conversion.'}
            </p>
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              <Plus size={15} /> Create Lead
            </button>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Event</th>
                  <th>Message</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {leads.map(lead => (
                  <tr
                    key={lead.id}
                    data-testid={`lead-row-${lead.id}`}
                    style={lead.status === 'new' ? { background: '#fffdf4' } : undefined}
                  >
                    <td style={{ fontWeight: 600 }}>
                      {lead.name}
                      {lead.converted_client_id && (
                        <div data-testid="lead-linked-client-indicator" style={{ marginTop: 4 }}>
                          <span className="badge badge-confirmed">Client linked</span>
                        </div>
                      )}
                    </td>
                    <td>
                      <div style={{ fontSize: 'var(--text-sm)' }}>{lead.email || '—'}</div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{lead.phone || ''}</div>
                    </td>
                    <td>
                      <div style={{ fontSize: 'var(--text-sm)', textTransform: 'capitalize' }}>{lead.event_type}</div>
                      {lead.event_date && (
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                          {format(new Date(lead.event_date + 'T00:00:00'), 'MMM d, yyyy')}
                        </div>
                      )}
                    </td>
                    <td>
                      {extractEmailSubject(lead.notes) && (
                        <div data-testid="lead-email-subject" style={{ maxWidth: 280, fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 4 }}>
                          Subject: {extractEmailSubject(lead.notes)}
                        </div>
                      )}
                      <div data-testid="lead-message-block" style={{ maxWidth: 280, fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {lead.notes || '—'}
                      </div>
                    </td>
                    <td>
                      <span data-testid="lead-source-badge" className="badge badge-draft" style={{ textTransform: 'capitalize' }}>
                        {lead.source || '—'}
                      </span>
                    </td>
                    <td>
                      <div style={{ marginBottom: 6 }}>
                        <span data-testid={lead.status === 'quoted' ? 'lead-status-quoted' : 'lead-status-badge'} className={`badge badge-${lead.status}`} style={{ textTransform: 'capitalize' }}>
                          {lead.status}
                        </span>
                      </div>
                      <select
                        data-testid="lead-status-select"
                        className="form-select"
                        value={lead.status}
                        onChange={async (e) => {
                          const next = e.target.value
                          await supabase.from('leads').update({ status: next }).eq('id', lead.id)
                          loadLeads()
                        }}
                        style={{ minWidth: 130 }}
                      >
                        {LEAD_STATUSES.map(s => (
                          <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                      {format(new Date(lead.created_at), 'MMM d, yyyy')}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {lead.status === 'lost' ? (
                          <button data-testid="lead-create-quote-disabled" className="btn btn-secondary btn-sm" disabled title="Cannot create quote from lost lead">
                            <FileText size={14} /> Create Quote
                          </button>
                        ) : (
                          <button data-testid="lead-create-quote-button" className="btn btn-secondary btn-sm" onClick={() => createQuoteFromLead(lead)} title="Create Quote from Lead">
                            <FileText size={14} /> Create Quote
                          </button>
                        )}
                        {lead.status !== 'converted' && lead.status !== 'lost' && (
                          <button data-testid="lead-convert-button" className="btn btn-primary btn-sm" onClick={() => convertToClient(lead)} title="Convert to Client">
                            <UserCheck size={14} /> Convert
                          </button>
                        )}
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditLead(lead)}>
                          Edit
                        </button>
                        <Link to={`/leads/${lead.id}`} className="btn btn-ghost btn-sm">
                          View <ChevronRight size={13} />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(showModal || editLead) && (
        <LeadModal
          lead={editLead}
          onClose={() => { setShowModal(false); setEditLead(null) }}
          onSaved={() => { setShowModal(false); setEditLead(null); loadLeads() }}
        />
      )}
    </div>
  )
}

// ─── Lead Detail ─────────────────────────────────────────────────
export function LeadDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [lead, setLead] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showEdit, setShowEdit] = useState(false)

  useEffect(() => {
    supabase.from('leads').select('*').eq('id', id).single().then(({ data, error }) => {
      if (error) navigate('/leads')
      else setLead(data)
      setLoading(false)
    })
  }, [id, navigate])

  async function ensureClientForLead() {
    if (lead.converted_client_id) return lead.converted_client_id

    const { data: existingClient } = await supabase
      .from('clients')
      .select('id')
      .eq('email', lead.email)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (existingClient?.id) {
      await supabase.from('leads').update({ converted_client_id: existingClient.id }).eq('id', id)
      setLead(prev => ({ ...prev, converted_client_id: existingClient.id }))
      return existingClient.id
    }

    const { data: newClient, error } = await supabase.from('clients').insert({
      name: lead.name,
      email: lead.email,
      phone: lead.phone,
      source: 'lead-conversion',
      notes: lead.notes,
    }).select().single()
    if (error) throw error

    await supabase.from('leads').update({ converted_client_id: newClient.id }).eq('id', id)
    setLead(prev => ({ ...prev, converted_client_id: newClient.id }))
    return newClient.id
  }

  async function convertToClient() {
    if (!window.confirm(`Convert "${lead.name}" to a client?`)) return
    try {
      const clientId = await ensureClientForLead()
      await supabase.from('leads').update({ status: 'converted', converted_client_id: clientId }).eq('id', id)
      navigate(`/clients/${clientId}`)
    } catch (error) {
      alert(error.message)
    }
  }

  async function createQuoteFromLead() {
    try {
      const clientId = await ensureClientForLead()
      const params = new URLSearchParams({ client: clientId, lead: id })
      navigate(`/quotes/new?${params.toString()}`)
    } catch (error) {
      alert(error.message)
    }
  }

  if (loading) return <div className="loading-state">Loading lead…</div>
  if (!lead) return null

  return (
    <div data-testid="lead-detail">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/leads')}>
            <ArrowLeft size={16} /> Back
          </button>
          <div>
            <h1 className="page-title">{lead.name}</h1>
            <p className="page-subtitle">Lead submitted {format(new Date(lead.created_at), 'MMMM d, yyyy')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {lead.status === 'lost' ? (
            <button data-testid="lead-create-quote-disabled" className="btn btn-secondary" disabled>
              <FileText size={16} /> Create Quote
            </button>
          ) : (
            <button data-testid="lead-create-quote-button" className="btn btn-secondary" onClick={createQuoteFromLead}>
              <FileText size={16} /> Create Quote
            </button>
          )}
          {lead.status !== 'converted' && lead.status !== 'lost' && (
            <button data-testid="lead-convert-button" className="btn btn-success" onClick={convertToClient}>
              <UserCheck size={16} /> Convert to Client
            </button>
          )}
          <button className="btn btn-primary btn-sm" onClick={() => setShowEdit(true)}>Edit</button>
        </div>
      </div>

      <div className="lead-detail-grid">
        <div className="card">
          <div className="card-header"><h3 style={{ fontWeight: 600 }}>Lead Information</h3></div>
          <div className="card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div><div className="form-label">Name</div><div style={{ marginTop: 4 }}>{lead.name}</div></div>
            <div>
              <div className="form-label">Status</div>
              <div style={{ marginTop: 4 }}><span data-testid={lead.status === 'quoted' ? 'lead-status-quoted' : 'lead-status-badge'} className={`badge badge-${lead.status}`}>{lead.status}</span></div>
              {lead.converted_client_id && (
                <div data-testid="lead-linked-client-indicator" style={{ marginTop: 6 }}><span className="badge badge-confirmed">Client linked</span></div>
              )}
            </div>
            <div><div className="form-label">Email</div><div style={{ marginTop: 4 }}>{lead.email || '—'}</div></div>
            <div><div className="form-label">Phone</div><div style={{ marginTop: 4 }}>{lead.phone || '—'}</div></div>
            <div><div className="form-label">Event Type</div><div style={{ marginTop: 4, textTransform: 'capitalize' }}>{lead.event_type}</div></div>
            <div><div className="form-label">Event Date</div><div style={{ marginTop: 4 }}>{lead.event_date ? format(new Date(lead.event_date + 'T00:00:00'), 'MMMM d, yyyy') : '—'}</div></div>
            <div><div className="form-label">Guest Count</div><div style={{ marginTop: 4 }}>{lead.guest_count ?? '—'}</div></div>
            <div><div className="form-label">Source</div><div style={{ marginTop: 4, textTransform: 'capitalize' }}>{lead.source}</div></div>
            {lead.notes && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div className="form-label">Notes</div>
                <div style={{ marginTop: 4, whiteSpace: 'pre-wrap', fontSize: 'var(--text-sm)' }}>{lead.notes}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showEdit && (
        <LeadModal
          lead={lead}
          onClose={() => setShowEdit(false)}
          onSaved={saved => { setLead(saved); setShowEdit(false) }}
        />
      )}

      <style>{`
        .lead-detail-grid { max-width: 720px; }
        .loading-state { padding: 60px; text-align: center; color: var(--color-text-muted); }
      `}</style>
    </div>
  )
}
